// FULL UPDATED FILE
import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import {
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Box,
    CircularProgress,
    TextField,
    Skeleton,
} from "@mui/material";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Swal from "sweetalert2";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";
import { ServerBaseUrlForStaticImages } from "constants";
import MDButton from "components/MDButton";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import BrokenImage from "@mui/icons-material/BrokenImage";
import VideocamIcon from "@mui/icons-material/Videocam";

// ---------------- SINGLE MEDIA ZOOM PREVIEW WITH SLIDER --------------
const MediaZoomModal = ({ open, onClose, mediaList, currentIndex, onPrev, onNext }) => {
    const media = mediaList[currentIndex];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1,
                }}
            >
                <MDTypography variant="body2">
                    {media?.title || "Preview"} ({currentIndex + 1}/{mediaList.length})
                </MDTypography>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    textAlign: "center",
                    bgcolor: "#000",
                    p: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                }}
            >
                {/* LEFT ARROW */}
                <IconButton
                    onClick={onPrev}
                    disabled={currentIndex === 0}
                    sx={{
                        position: "absolute",
                        left: 16,
                        top: "50%",
                        transform: "translateY(-50%)",
                        bgcolor: "rgba(0,0,0,0.4)",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                    }}
                >
                    <ArrowBackIosNewIcon sx={{ color: "white" }} />
                </IconButton>

                {/* MEDIA */}
                {media && (
                    <>
                        {media.type === "image" ? (
                            <img
                                src={media.src}
                                alt={media.title || "preview"}
                                style={{
                                    maxHeight: "90vh",
                                    width: "auto",
                                    objectFit: "contain",
                                }}
                            />
                        ) : (
                            <video
                                controls
                                style={{
                                    maxHeight: "90vh",
                                    width: "auto",
                                    objectFit: "contain",
                                }}
                            >
                                <source src={media.src} type="video/mp4" />
                            </video>
                        )}
                    </>
                )}

                {/* RIGHT ARROW */}
                <IconButton
                    onClick={onNext}
                    disabled={currentIndex === mediaList.length - 1}
                    sx={{
                        position: "absolute",
                        right: 16,
                        top: "50%",
                        transform: "translateY(-50%)",
                        bgcolor: "rgba(0,0,0,0.4)",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                    }}
                >
                    <ArrowForwardIosIcon sx={{ color: "white" }} />
                </IconButton>
            </DialogContent>
        </Dialog>
    );
};

// ================= MEDIA ITEM (THUMBNAIL) ==================
const MediaItem = ({ src, alt, type, onImageClick, onDetailClick }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    return (
        <Grid item xs={12} sm={6} md={4}>
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: 230, // tall card for full dress view
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "10px",
                    overflow: "hidden",
                    cursor: "pointer",
                    bgcolor: "#ffffff",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                    "& img, & video": {
                        transition: "transform 0.25s ease",
                    },
                    "&:hover img, &:hover video": {
                        transform: "scale(1.05)", // hover zoom
                    },
                }}
            >
                {/* SHIMMER / SKELETON WHILE LOADING */}
                {loading && !error && (
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{ position: "absolute", top: 0, left: 0 }}
                    />
                )}

                {/* Error icon */}
                {error && (
                    <Box sx={{ position: "absolute" }}>
                        {type === "image" ? (
                            <BrokenImage sx={{ color: "error.main", fontSize: 42 }} />
                        ) : (
                            <VideocamIcon sx={{ color: "error.main", fontSize: 42 }} />
                        )}
                    </Box>
                )}

                {/* Media Rendering */}
                {type === "image" ? (
                    <img
                        src={src}
                        alt={alt || "Product"}
                        onLoad={() => setLoading(false)}
                        onError={() => {
                            setLoading(false);
                            setError(true);
                        }}
                        onClick={onImageClick}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            backgroundColor: "#ffffff",
                        }}
                    />
                ) : (
                    <video
                        muted
                        playsInline
                        preload="metadata"
                        onLoadedData={() => setLoading(false)}
                        onError={() => {
                            setLoading(false);
                            setError(true);
                        }}
                        onClick={onImageClick}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            backgroundColor: "#ffffff",
                        }}
                    >
                        <source src={src} type="video/mp4" />
                    </video>
                )}

                {/* VIEW PRODUCT DETAILS (eye) */}
                {onDetailClick && (
                    <IconButton
                        sx={{
                            position: "absolute",
                            top: 5,
                            right: 5,
                            bgcolor: "white",
                            boxShadow: 3,
                            "&:hover": { bgcolor: "#f0f0f0" },
                        }}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDetailClick();
                        }}
                    >
                        <VisibilityIcon fontSize="small" color="primary" />
                    </IconButton>
                )}
            </Box>

            {/* PRODUCT NAME UNDER IMAGE */}
            <MDTypography
                variant="body2"
                textAlign="center"
                display="block"
                mt={1}
                sx={{ fontWeight: 500 }}
                noWrap
            >
                {alt || "Product"}
            </MDTypography>
        </Grid>
    );
};

// ================= MEDIA MODAL (GRID LISTING) ==================
const MediaModal = ({
    open,
    onClose,
    selectedProductImages = [],
    selectedProductVideos = [],
    onZoomIndex,
    openDetails,
    showDetailsIcon,
}) => {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle
                sx={{
                    bgcolor: "info.main",
                    color: "white",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1.5,
                }}
            >
                <MDTypography variant="subtitle2" color="white">
                    Product Media Gallery
                </MDTypography>
                <IconButton onClick={onClose} sx={{ color: "white" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <MDTypography variant="subtitle1" sx={{ mb: 1 }}>
                    Images
                </MDTypography>
                <Grid container spacing={2}>
                    {selectedProductImages.length ? (
                        selectedProductImages.map((item, index) => (
                            <MediaItem
                                key={`img-${index}`}
                                src={item.src}
                                type="image"
                                alt={item.name}
                                onImageClick={() => onZoomIndex(index)}
                                onDetailClick={showDetailsIcon ? openDetails : undefined}
                            />
                        ))
                    ) : (
                        <Grid item xs={12}>
                            <MDTypography variant="body2" color="text.secondary">
                                No Images
                            </MDTypography>
                        </Grid>
                    )}
                </Grid>

                <MDTypography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                    Videos
                </MDTypography>
                <Grid container spacing={2}>
                    {selectedProductVideos.length ? (
                        selectedProductVideos.map((item, index) => (
                            <MediaItem
                                key={`vid-${index}`}
                                src={item.src}
                                type="video"
                                alt={item.name}
                                onImageClick={() =>
                                    onZoomIndex(selectedProductImages.length + index)
                                }
                                onDetailClick={showDetailsIcon ? openDetails : undefined}
                            />
                        ))
                    ) : (
                        <Grid item xs={12}>
                            <MDTypography variant="body2" color="text.secondary">
                                No Videos
                            </MDTypography>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions>
                <MDButton onClick={onClose} color="primary" variant="contained">
                    Close
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

// ================= MAIN PAGE ==================
function VendorAdminProducts() {
    const navigate = useNavigate();
    const email = localStorage.getItem("email");

    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState("all");
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    const [searchQuery, setSearchQuery] = useState("");

    const [openImageModal, setOpenImageModal] = useState(false);
    const [openContentModel, setOpenContentModel] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const [selectedProductImages, setSelectedProductImages] = useState([]); // [{src,name,type}]
    const [selectedProductVideos, setSelectedProductVideos] = useState([]); // [{src,name,type}]

    // For slider preview
    const [zoomMediaList, setZoomMediaList] = useState([]); // [{src,type,title}]
    const [zoomIndex, setZoomIndex] = useState(0);
    const [zoomOpen, setZoomOpen] = useState(false);

    // pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        disableBackNavigation();
        fetchVendors();
        fetchProducts();
    }, []);

    const fetchVendors = async () => {
        try {
            const res = await api.get(`/vendor_register/fetch_approved`);
            setVendors(res?.data?.vendors || []);
        } catch (error) {
            console.error("Error fetching vendors:", error);
        }
    };

    const fetchProducts = async (vendorEmailParam = "all") => {
        setLoading(true);
        try {
            const reqEmail = vendorEmailParam === "all" ? email : vendorEmailParam;
            const res = await api.get(`/product/fetch_all_products`, {
                params: { email: reqEmail },
            });
            const list = res.data.products || [];
            setProducts(list);
            setFilteredProducts(list);
            setPage(0);
        } catch (error) {
            console.error("Error loading products:", error);
            Swal.fire("Error", "Failed to load products", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleVendorChange = (e) => {
        const vendorId = e.target.value;
        setSelectedVendor(vendorId);

        if (vendorId === "all") {
            fetchProducts();
        } else {
            const vendor = vendors.find((item) => item.id === vendorId);
            if (vendor?.email) {
                fetchProducts(vendor.email);
            }
        }
    };

    // Vendor-wide media preview (all media of that vendor)
    const handleVendorPreview = async () => {
        if (!selectedVendor || selectedVendor === "all") {
            Swal.fire("No Vendor Selected", "Please select a vendor!", "warning");
            return;
        }

        const vendor = vendors.find((v) => v.id === selectedVendor);
        if (!vendor) return;

        try {
            const res = await api.get(`/product/fetch_all_products`, {
                params: { email: vendor.email },
            });

            const vendorProducts = res.data.products || [];

            const allMedia = vendorProducts.flatMap((product) => {
                const images = Array.isArray(product.product_images)
                    ? product.product_images.map((img) => ({
                        src: `${ServerBaseUrlForStaticImages}/product/media/${img}`,
                        name: product.product_name,
                        type: "image",
                    }))
                    : [];

                const videos = Array.isArray(product.product_videos)
                    ? product.product_videos.map((vid) => ({
                        src: `${ServerBaseUrlForStaticImages}/product/media/${vid}`,
                        name: product.product_name,
                        type: "video",
                    }))
                    : [];

                return [...images, ...videos];
            });

            if (!allMedia.length) {
                Swal.fire("No Media!", "This vendor has not uploaded any product media.", "info");
                return;
            }

            const allImages = allMedia.filter((m) => m.type === "image");
            const allVideos = allMedia.filter((m) => m.type === "video");

            const mediaList = allMedia.map((m) => ({
                src: m.src,
                type: m.type,
                title: m.name || "Vendor Media",
            }));

            setZoomMediaList(mediaList);
            setZoomIndex(0);
            setSelectedProduct(null);
            setSelectedProductImages(allImages);
            setSelectedProductVideos(allVideos);
            setOpenImageModal(true);
        } catch (error) {
            console.error("Error fetching vendor media:", error);
            Swal.fire("Error", "Unable to fetch media", "error");
        }
    };

    // Search
    useEffect(() => {
        const s = searchQuery.toLowerCase();
        const filtered = products.filter(
            (p) =>
                p.product_name?.toLowerCase().includes(s) ||
                p.description?.toLowerCase().includes(s) ||
                p.category_id?.toString().toLowerCase().includes(s) ||
                p.price?.toString().toLowerCase().includes(s) ||
                p.sku?.toLowerCase().includes(s)
        );
        setFilteredProducts(filtered);
        setPage(0);
    }, [searchQuery, products]);

    const openDetails = (prod) => {
        if (!prod) return;
        setSelectedProduct(prod);
        setOpenContentModel(true);
    };

    const handleViewMedia = (product) => {
        if (!product) return;
        setSelectedProduct(product);

        const imgs = Array.isArray(product.product_images)
            ? product.product_images.map((img) => ({
                src: `${ServerBaseUrlForStaticImages}/product/media/${img}`,
                name: product.product_name,
                type: "image",
            }))
            : [];

        const vids = Array.isArray(product.product_videos)
            ? product.product_videos.map((vid) => ({
                src: `${ServerBaseUrlForStaticImages}/product/media/${vid}`,
                name: product.product_name,
                type: "video",
            }))
            : [];

        const mediaList = [...imgs, ...vids].map((m) => ({
            src: m.src,
            type: m.type,
            title: m.name,
        }));

        setZoomMediaList(mediaList);
        setZoomIndex(0);
        setSelectedProductImages(imgs);
        setSelectedProductVideos(vids);
        setOpenImageModal(true);
    };

    // open zoom slider at specific index
    const openZoomAtIndex = (index) => {
        if (!zoomMediaList.length) return;
        setZoomIndex(index);
        setZoomOpen(true);
    };

    const handleCloseZoom = () => setZoomOpen(false);

    const handlePrevZoom = () => {
        setZoomIndex((prev) => (prev > 0 ? prev - 1 : prev));
    };

    const handleNextZoom = () => {
        setZoomIndex((prev) =>
            prev < zoomMediaList.length - 1 ? prev + 1 : prev
        );
    };

    // pagination helpers (match DataTable signature)
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event, newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const paginatedTableData = useMemo(
        () =>
            filteredProducts.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
            ),
        [filteredProducts, page, rowsPerPage]
    );

    const columns = useMemo(
        () => [
            {
                Header: "S.No",
                accessor: "s_no",
                align: "center",
                Cell: ({ row }) => page * rowsPerPage + row.index + 1,
            },
            { Header: "Product Name", accessor: "product_name" },
            { Header: "Category", accessor: "category_id" },
            { Header: "Price", accessor: "price" },
            {
                Header: "Media",
                accessor: "media",
                Cell: ({ row }) => (
                    <IconButton
                        onClick={() => handleViewMedia(row.original)}
                        title="View Product Media"
                    >
                        <ImageIcon color="primary" />
                    </IconButton>
                ),
            },
            {
                Header: "Details",
                accessor: "details",
                Cell: ({ row }) => (
                    <IconButton
                        onClick={() => openDetails(row.original)}
                        title="View Product Details"
                    >
                        <VisibilityIcon color="info" />
                    </IconButton>
                ),
            },
        ],
        [page, rowsPerPage]
    );

    return (
        <DashboardLayout>
            <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                <LogoutButton
                    onLogout={() => {
                        navigate("/login");
                    }}
                />
            </MDBox>

            <DashboardNavbar />

            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            {/* Header */}
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                            >
                                <MDTypography variant="h6" color="white">
                                    Products
                                </MDTypography>
                            </MDBox>

                            {/* Filters Row */}
                            <MDBox px={3} pt={3} pb={1}>
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={2}
                                    flexWrap="wrap"
                                    justifyContent="space-between"
                                >
                                    {/* Vendor Select */}
                                    <FormControl
                                        sx={{
                                            minWidth: 230,
                                            "& .MuiOutlinedInput-root": {
                                                height: 46,
                                                borderRadius: 2,
                                            },
                                        }}
                                    >
                                        <InputLabel id="vendor-select-label">
                                            Select Vendor
                                        </InputLabel>
                                        <Select
                                            labelId="vendor-select-label"
                                            value={selectedVendor}
                                            onChange={handleVendorChange}
                                            label="Select Vendor"
                                        >
                                            <MenuItem value="all">All Vendors</MenuItem>
                                            {vendors.map((v) => (
                                                <MenuItem key={v.id} value={v.id}>
                                                    {v.owner_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {/* Search Box */}
                                    <TextField
                                        label="Search Products"
                                        variant="outlined"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        sx={{
                                            flexGrow: 1,
                                            minWidth: 260,
                                            "& .MuiOutlinedInput-root": {
                                                height: 46,
                                                borderRadius: 2,
                                            },
                                        }}
                                    />

                                    {/* Preview Vendor Media */}
                                    <Box textAlign="center">
                                        <IconButton
                                            disabled={selectedVendor === "all"}
                                            onClick={handleVendorPreview}
                                            sx={{
                                                borderRadius: 2,
                                                border: "1px solid",
                                                borderColor:
                                                    selectedVendor === "all"
                                                        ? "grey.300"
                                                        : "info.main",
                                                bgcolor: "white",
                                            }}
                                        >
                                            <VisibilityIcon
                                                color={
                                                    selectedVendor === "all" ? "disabled" : "primary"
                                                }
                                            />
                                        </IconButton>
                                        <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            mt={0.5}
                                        >
                                            Preview Vendor Media
                                        </MDTypography>
                                    </Box>
                                </MDBox>
                            </MDBox>

                            {/* Table */}
                            <MDBox p={3}>
                                {loading ? (
                                    <Box display="flex" justifyContent="center" py={5}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <DataTable
                                        table={{ columns, rows: paginatedTableData }}
                                        isSorted={true}
                                        entriesPerPage={{
                                            defaultValue: rowsPerPage,
                                            entries: [5, 10, 15, 20, 25],
                                        }}
                                        showTotalEntries={true}
                                        canSearch={false}
                                        noEndBorder
                                        pagination={{
                                            variant: "contained",
                                            color: "dark",
                                            page,
                                            rowsPerPage,
                                            onPageChange: handlePageChange,
                                            onRowsPerPageChange: handleRowsPerPageChange,
                                            count: filteredProducts.length,
                                        }}
                                    />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* MEDIA VIEWER MODAL */}
            <MediaModal
                open={openImageModal}
                onClose={() => setOpenImageModal(false)}
                selectedProductImages={selectedProductImages}
                selectedProductVideos={selectedProductVideos}
                onZoomIndex={openZoomAtIndex}
                openDetails={() => openDetails(selectedProduct)}
                showDetailsIcon={Boolean(selectedProduct)}
            />

            {/* ZOOM PREVIEW WITH SLIDER */}
            {zoomMediaList.length > 0 && (
                <MediaZoomModal
                    open={zoomOpen}
                    onClose={handleCloseZoom}
                    mediaList={zoomMediaList}
                    currentIndex={zoomIndex}
                    onPrev={handlePrevZoom}
                    onNext={handleNextZoom}
                />
            )}

            {/* PRODUCT DETAILS MODAL */}
            <Dialog
                open={openContentModel}
                onClose={() => setOpenContentModel(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle
                    sx={{
                        backgroundColor: "info.main",
                        color: "white",
                        p: 1.5,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <MDTypography variant="subtitle1" fontWeight="bold" color="white">
                        Product Details
                    </MDTypography>
                    <IconButton
                        sx={{ color: "white" }}
                        onClick={() => setOpenContentModel(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ backgroundColor: "#f9f9f9" }}>
                    {selectedProduct ? (
                        <Box
                            sx={{ backgroundColor: "white", borderRadius: 1, p: 2, boxShadow: 1 }}
                        >
                            <MDTypography
                                variant="subtitle1"
                                fontWeight="bold"
                                color="primary"
                                gutterBottom
                            >
                                {selectedProduct.product_name}
                            </MDTypography>

                            <MDTypography variant="body2" color="text.secondary" mb={2}>
                                {selectedProduct.description || "No description available."}
                            </MDTypography>

                            <Grid container spacing={1} mb={2}>
                                <Grid item xs={12} sm={6}>
                                    <MDTypography variant="caption" color="text.secondary">
                                        <strong>Category:</strong> {selectedProduct.category_id}
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MDTypography variant="caption" color="text.secondary">
                                        <strong>SKU:</strong> {selectedProduct.sku}
                                    </MDTypography>
                                </Grid>
                            </Grid>

                            <Box mb={2}>
                                <MDTypography
                                    variant="subtitle2"
                                    fontWeight="medium"
                                    color="primary"
                                >
                                    Price: ₹{selectedProduct.price}
                                </MDTypography>
                                {selectedProduct.discount_price && (
                                    <MDTypography variant="caption" color="text.secondary">
                                        Discount: ₹{selectedProduct.discount_price} (
                                        {selectedProduct.discount}% Off)
                                    </MDTypography>
                                )}
                            </Box>

                            <Grid container spacing={1} mb={2}>
                                <Grid item xs={12} sm={6}>
                                    <MDTypography variant="caption" color="text.primary">
                                        <strong>Stock:</strong> {selectedProduct.stock_quantity}
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MDTypography
                                        variant="caption"
                                        color={
                                            selectedProduct.status === "active"
                                                ? "success.main"
                                                : "error.main"
                                        }
                                    >
                                        <strong>Status:</strong>{" "}
                                        {selectedProduct.status?.toUpperCase() || "N/A"}
                                    </MDTypography>
                                </Grid>
                            </Grid>

                            <Box mb={2}>
                                <MDTypography
                                    variant="subtitle2"
                                    color="info.main"
                                    fontWeight="medium"
                                >
                                    GST Information
                                </MDTypography>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6}>
                                        <MDTypography variant="caption" color="text.secondary">
                                            <strong>Percentage:</strong>{" "}
                                            {selectedProduct.gst_percentage}%
                                        </MDTypography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <MDTypography variant="caption" color="text.secondary">
                                            <strong>Type:</strong> {selectedProduct.gst_type}
                                        </MDTypography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    ) : (
                        <MDTypography variant="body2" color="error">
                            No product selected.
                        </MDTypography>
                    )}
                </DialogContent>

                <DialogActions sx={{ backgroundColor: "info.light", p: 1.5 }}>
                    <MDButton
                        onClick={() => setOpenContentModel(false)}
                        color="primary"
                        variant="contained"
                    >
                        Close
                    </MDButton>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}

export default VendorAdminProducts;
